import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2, Minus, Plus } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { SignaturePad, type SignaturePadHandle } from '@/components/audit/SignaturePad';
import {
  EMPTY_ADDR,
  Field,
  Section,
  type StructuredAddr,
  StructuredAddressField,
  Textarea,
  addrBlob,
  addrComplete,
  addrJson,
} from '@/components/apply/ApplyFields';
import {
  APPLY_CHAIN_THEME_CLASS,
  AlamedaSodaTheme,
} from '@/components/apply/alamedaSodaTheme';
import { APPLICATION_TERMS, TERMS_VERSION } from '@/lib/applicationTerms';
import { DAY_NAMES } from '@/lib/deliverySchedule';
import { EQUIPMENT_CHOICES } from '@/lib/equipmentInterest';
import { SUPPORT_PHONE, SUPPORT_TEL_HREF } from '@/lib/constants';

/**
 * /apply/:token — the CHAIN / FRANCHISE new-store application. Public, no
 * login, no app shell (a sibling of /apply).
 *
 * One link per GROUP: corporate (or a franchise owner company) pastes it into
 * its own onboarding packet and every new location uses the same URL, saying
 * on the form which one it is. The token is the only credential — nothing here
 * is prefilled from an existing store, deliberately, because a public URL must
 * never display one customer's address or contacts to whoever is holding it.
 *
 * It asks for the small set of things genuinely unknown about a new location.
 * Payment terms, payment method, billing address, accounting email, invoice
 * routing and pricing all come from the group's master account and are never
 * shown — the whole reason a chain store shouldn't be sent to /apply, which is
 * a credit application asking for a dozen things we already hold.
 *
 * WALKTHROUGH, not a wall. The form is stepped so the person filling it in
 * sees four short questions instead of one long document; each step validates
 * before it will advance, so an error lands next to the field that caused it
 * rather than at the bottom of a page they've scrolled past.
 *
 * EQUIPMENT (franchise links, `ask_for.equipment`). A franchisee buys or leases
 * their own equipment, so we ask while they're here and hand them to the
 * leasing app at the end. A corporate store manager can't commit to a lease on
 * the chain's behalf, so their link skips it entirely.
 */

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface AskFor {
  store_code: boolean;
  store_phone: boolean;
  gm_contact: boolean;
  open_date: boolean;
  delivery_days: boolean;
  special_instructions: boolean;
  terms: boolean;
  signature: boolean;
  equipment: boolean;
}

interface LinkInfo {
  usable: boolean;
  chain_name?: string;
  /**
   * Which kind of account this link belongs to.
   *
   *   'chain'     — the brand's corporate master. Anyone in the brand may hold
   *                 this link, so the page stays generic.
   *   'franchise' — ONE franchisee owner company that already buys from us.
   *                 The link is the identity, so the page can name them and
   *                 say plainly that nothing will be asked twice.
   */
  owner_kind?: 'chain' | 'franchise' | null;
  label?: string | null;
  ask_for?: AskFor;
  message?: string;
}

const DAY_LABELS: Record<string, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

interface FormState {
  store_name: string;
  store_code: string;
  store_phone: string;
  opens_on: string;
  gm_name: string;
  gm_email: string;
  gm_phone: string;
  special_instructions: string;
  equipment_notes: string;
  notes: string;
  authorized_by: string;
}

const EMPTY: FormState = {
  store_name: '',
  store_code: '',
  store_phone: '',
  opens_on: '',
  gm_name: '',
  gm_email: '',
  gm_phone: '',
  special_instructions: '',
  equipment_notes: '',
  notes: '',
  authorized_by: '',
};

type StepId = 'store' | 'contact' | 'deliveries' | 'equipment' | 'review';

export default function ApplyChain() {
  const { token = '' } = useParams<{ token: string }>();

  const [link, setLink] = useState<LinkInfo | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [addr, setAddr] = useState<StructuredAddr>({ ...EMPTY_ADDR });
  const [days, setDays] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<Record<string, number>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const sigRef = useRef<SignaturePadHandle | null>(null);
  const [signed, setSigned] = useState(false);
  const [hpField, setHpField] = useState(''); // honeypot — must stay empty
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);

  const ask = link?.ask_for;

  // Which steps this link actually has. Deliveries collapses out when the
  // chain turned both of its questions off, equipment only exists on links
  // that ask for it — so nobody ever clicks Continue through an empty screen.
  const steps = useMemo<Array<{ id: StepId; label: string }>>(() => {
    const out: Array<{ id: StepId; label: string }> = [
      { id: 'store', label: 'The location' },
      { id: 'contact', label: 'Who runs it' },
    ];
    if (ask?.delivery_days || ask?.special_instructions) {
      out.push({ id: 'deliveries', label: 'Deliveries' });
    }
    if (ask?.equipment) out.push({ id: 'equipment', label: 'Equipment' });
    out.push({ id: 'review', label: 'Review & send' });
    return out;
  }, [ask]);

  const step = steps[Math.min(stepIdx, steps.length - 1)]?.id ?? 'store';
  const isLast = stepIdx >= steps.length - 1;

  // Resolve the link. A revoked, expired or unknown token all come back the
  // same way — usable:false plus a line telling the holder who to call.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/.netlify/functions/onboarding-link?token=${encodeURIComponent(token)}`,
        );
        const data = (await res.json().catch(() => ({}))) as LinkInfo;
        if (!cancelled) setLink(data);
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Turnstile, only when a site key is configured. It lives on the LAST step,
  // so it renders when that step mounts rather than when the link resolves.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !link?.usable || step !== 'review') return;
    const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    let rendered = false;

    function tryRender() {
      if (rendered || !turnstileRef.current || !window.turnstile) return;
      rendered = true;
      window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY!,
        callback: (t: string) => setTurnstileToken(t),
        'expired-callback': () => setTurnstileToken(''),
      });
    }

    if (window.turnstile) {
      tryRender();
      return;
    }
    let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', tryRender);
    const poll = window.setInterval(tryRender, 300);
    return () => {
      script?.removeEventListener('load', tryRender);
      window.clearInterval(poll);
    };
  }, [link?.usable, step]);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleDay(day: string) {
    setDays((d) => (d.includes(day) ? d.filter((x) => x !== day) : [...d, day]));
  }

  function setQty(category: string, qty: number) {
    setEquipment((e) => {
      const next = { ...e };
      if (qty <= 0) delete next[category];
      else next[category] = Math.min(99, qty);
      return next;
    });
  }

  const equipmentLines = useMemo(
    () =>
      EQUIPMENT_CHOICES.filter((c) => (equipment[c.category] ?? 0) > 0).map((c) => ({
        category: c.category,
        label: c.label,
        qty: equipment[c.category]!,
      })),
    [equipment],
  );

  /** Validate just the step being left — errors land next to their field. */
  function validateStep(id: StepId): string | null {
    if (id === 'store') {
      if (!form.store_name.trim()) return 'Please enter the location name.';
      if (!addrComplete(addr)) {
        return 'Please complete the delivery address — street, city, state and ZIP.';
      }
    }
    if (id === 'contact') {
      if (!form.gm_name.trim()) {
        return ask?.gm_contact ? "Please enter the manager's name." : 'Please enter a contact name.';
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.gm_email.trim())) {
        return 'Please enter a valid email address — we send the account details there.';
      }
    }
    if (id === 'review') {
      if (ask?.terms && !termsAccepted) return 'Please review and accept the Terms & Conditions.';
      if (ask?.terms && !form.authorized_by.trim()) {
        return 'Please enter the name and title of the person authorizing this application.';
      }
      if (ask?.signature && !sigRef.current?.toDataURL()) {
        return 'Please sign in the signature box.';
      }
    }
    return null;
  }

  function goNext() {
    const problem = validateStep(step);
    if (problem) return setError(problem);
    setError(null);
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function goBack() {
    setError(null);
    setStepIdx((i) => Math.max(0, i - 1));
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * ONE submit handler for the whole walkthrough, and the primary button is
   * ALWAYS type="submit" — it routes to "next step" until the last one.
   *
   * Two reasons, one of them a bug this shape prevents. React reuses the same
   * DOM node for the primary button across steps, so a button that switched
   * type="button" → type="submit" on the final step would flip DURING the
   * click's own dispatch: the handler advances the step, React re-renders the
   * node as a submit button, and the browser then runs the submit default
   * action on it — sending a half-filled application from the step before.
   * (Observed: Continue on the equipment step submitted the form.) A type that
   * never changes can't do that. It also makes Enter in a text field advance
   * the step, which is what a keyboard user expects anyway.
   */
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ask) return;

    if (!isLast) {
      goNext();
      return;
    }
    setError(null);

    // Re-check EVERY step, not just the visible one — a step can be skipped
    // backwards and edited, and the server validates all of it anyway.
    for (const s of steps) {
      const problem = validateStep(s.id);
      if (problem) {
        setStepIdx(steps.findIndex((x) => x.id === s.id));
        return setError(problem);
      }
    }

    const signatureUrl = ask.signature ? sigRef.current?.toDataURL() ?? null : null;

    setSubmitting(true);
    try {
      const res = await fetch('/.netlify/functions/onboarding-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link_token: token,
          // The location IS the account being applied for, and its manager is
          // the application's primary contact — the same two fields a direct
          // application uses, so everything downstream is unchanged.
          business_name: form.store_name.trim(),
          contact_name: form.gm_name.trim(),
          email: form.gm_email.trim(),
          phone: form.gm_phone.trim() || undefined,
          store_code: form.store_code.trim() || undefined,
          store_phone: form.store_phone.trim() || undefined,
          opens_on: form.opens_on || undefined,
          preferred_delivery_days: days,
          special_instructions: form.special_instructions.trim() || undefined,
          notes: form.notes.trim() || undefined,
          delivery_address: addrBlob(addr),
          delivery_address_json: addrJson(addr),
          delivery_addresses: [{ label: 'Delivery', ...addrJson(addr) }],
          ...(ask.equipment && equipmentLines.length > 0
            ? {
                equipment_interest: equipmentLines.map((l, i) => ({
                  category: l.category,
                  qty: l.qty,
                  // The free-text note rides on the first line so it reaches
                  // the operator without inventing a second storage field.
                  ...(i === 0 && form.equipment_notes.trim()
                    ? { notes: form.equipment_notes.trim() }
                    : {}),
                })),
              }
            : {}),
          ...(ask.terms
            ? { terms_accepted: termsAccepted, terms_version: TERMS_VERSION, authorized_by: form.authorized_by }
            : {}),
          ...(signatureUrl
            ? { signature: { data: signatureUrl.replace(/^data:image\/png;base64,/, '') } }
            : {}),
          hp_field: hpField, // honeypot
          turnstile_token: turnstileToken,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        setError(data.error ?? 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setError('Could not reach the server. Please try again.');
      setSubmitting(false);
    }
  }

  const chainName = link?.chain_name ?? 'your chain';
  /**
   * A franchise link belongs to ONE owner company that already buys from us,
   * so the page can name them and promise nothing gets asked twice. A chain
   * link is held by the whole brand, so it stays generic ("New <brand> store")
   * and never claims to know which operator is filling it in.
   */
  const isFranchise = link?.owner_kind === 'franchise';
  // Brand names carry their own article ("The Melt"), so "New The Melt store"
  // and "the The Melt account" both fall out of naive interpolation. Reach for
  // the possessive, and reshape the heading when the name starts with "The".
  const owned = `${chainName}${/s$/i.test(chainName) ? "'" : "'s"}`;
  const startsWithThe = /^the\s/i.test(chainName);
  const heading = isFranchise
    ? `New location for ${chainName}`
    : startsWithThe
      ? `New store for ${chainName}`
      : `New ${chainName} store`;
  const introCopy = isFranchise
    ? `Tell us about the location and we'll add it to ${owned} account. Billing, pricing and payment stay exactly as they are on your existing locations.`
    : `Tell us about the store and we'll get it set up for ordering. Billing, pricing and payment all stay on ${owned} account.`;

  return (
    <div
      className={`${APPLY_CHAIN_THEME_CLASS} relative min-h-screen w-full flex items-start justify-center px-4 py-10`}
    >
      <AlamedaSodaTheme />
      <div className="as-grain absolute inset-0" aria-hidden />

      <div ref={topRef} className="relative w-full max-w-2xl">
        <div className="mb-7">
          <div className="as-eyebrow">Alameda Soda Co · Brix Beverage</div>
          <div
            className="as-display mt-1"
            style={{ fontSize: '26px', lineHeight: 1.15, color: 'var(--as-ink)' }}
          >
            Small-batch soda, made at Hangar 25
          </div>
          <p className="mt-1.5 text-[13px]" style={{ color: 'var(--as-muted)' }}>
            Pure cane sugar · No high-fructose corn syrup · Gluten free
          </p>
        </div>

        <div className="as-card overflow-hidden">
          <div className="h-1.5" style={{ background: 'var(--as-accent)' }} aria-hidden />

          <div className="p-6">
            {loadFailed ? (
              <Notice
                title="We couldn't load this form"
                body="Check your connection and refresh the page."
              />
            ) : !link ? (
              <div className="py-12 text-center" style={{ color: 'var(--as-muted)' }}>
                <Loader2 size={22} className="animate-spin mx-auto mb-3" />
                <p className="text-sm">Loading…</p>
              </div>
            ) : !link.usable || !ask ? (
              <Notice title="This link isn't active" body={link.message ?? ''} />
            ) : done ? (
              <Done
                storeName={form.store_name.trim()}
                email={form.gm_email.trim()}
                isFranchise={isFranchise}
                equipmentCount={ask.equipment ? equipmentLines.length : 0}
              />
            ) : (
              <>
                <h1
                  className="as-display"
                  style={{ fontSize: '22px', lineHeight: 1.2, color: 'var(--as-ink)' }}
                >
                  {heading}
                </h1>
                <p className="text-sm mt-1.5" style={{ color: 'var(--as-muted)' }}>
                  {introCopy}
                </p>

                <StepBar steps={steps} current={stepIdx} />

                <form onSubmit={onSubmit} className="space-y-7" noValidate>
                  {/* Honeypot — display:none (not just off-screen), so autofill
                      engines never see or fill it. A field named/positioned like
                      "Company"/off-canvas-only is a well-known autofill trigger —
                      it must not read as one to a real browser. */}
                  <div aria-hidden style={{ display: 'none' }}>
                    <label htmlFor="hp_field">Leave this field blank</label>
                    <input
                      id="hp_field"
                      name="hp_field"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={hpField}
                      onChange={(e) => setHpField(e.target.value)}
                    />
                  </div>

                  {step === 'store' && (
                    <Section title="The location">
                      <Field
                        label="Location name"
                        required
                        hint="However it's known — e.g. San Marcos."
                      >
                        <Input
                          value={form.store_name}
                          onChange={(e) => update('store_name', e.target.value)}
                          autoFocus
                        />
                      </Field>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {ask.store_code && (
                          <Field
                            label="Store number"
                            hint="Your own number for this location, if it has one."
                          >
                            <Input
                              value={form.store_code}
                              onChange={(e) => update('store_code', e.target.value)}
                              placeholder="e.g. 0055"
                            />
                          </Field>
                        )}
                        {ask.store_phone && (
                          <Field label="Store phone" hint="The line a driver can call.">
                            <Input
                              type="tel"
                              value={form.store_phone}
                              onChange={(e) => update('store_phone', e.target.value)}
                            />
                          </Field>
                        )}
                      </div>
                      {ask.open_date && (
                        <Field
                          label="Opening date"
                          hint="Roughly is fine — it tells us when to start deliveries."
                        >
                          <Input
                            type="date"
                            value={form.opens_on}
                            onChange={(e) => update('opens_on', e.target.value)}
                          />
                        </Field>
                      )}
                      <StructuredAddressField
                        label="Delivery address"
                        required
                        hint="Where the product actually gets dropped off."
                        addr={addr}
                        onChange={setAddr}
                      />
                    </Section>
                  )}

                  {step === 'contact' && (
                    <Section
                      title={ask.gm_contact ? 'Who runs the location' : 'Contact'}
                      subtitle={
                        ask.gm_contact
                          ? "The general manager. We'll email them the login for placing orders."
                          : "We'll email this person the login for placing orders."
                      }
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label={ask.gm_contact ? 'Manager name' : 'Contact name'} required>
                          <Input
                            value={form.gm_name}
                            onChange={(e) => update('gm_name', e.target.value)}
                            autoComplete="name"
                            autoFocus
                          />
                        </Field>
                        <Field label="Email" required>
                          <Input
                            type="email"
                            value={form.gm_email}
                            onChange={(e) => update('gm_email', e.target.value)}
                            autoComplete="email"
                          />
                        </Field>
                      </div>
                      <Field label="Mobile / direct phone">
                        <Input
                          type="tel"
                          value={form.gm_phone}
                          onChange={(e) => update('gm_phone', e.target.value)}
                          autoComplete="tel"
                        />
                      </Field>
                    </Section>
                  )}

                  {step === 'deliveries' && (
                    <Section title="Deliveries">
                      {ask.delivery_days && (
                        <Field
                          label="Which days suit you?"
                          hint="We'll match it to the delivery route for your area and confirm."
                        >
                          <div className="flex flex-wrap gap-2">
                            {DAY_NAMES.map((day) => {
                              const on = days.includes(day);
                              return (
                                <button
                                  key={day}
                                  type="button"
                                  aria-pressed={on}
                                  onClick={() => toggleDay(day)}
                                  className="px-3 py-1.5 rounded-lg text-[13px] font-semibold border cursor-pointer transition-colors"
                                  style={
                                    on
                                      ? {
                                          background: 'var(--as-accent-soft)',
                                          borderColor: 'var(--as-accent)',
                                          color: 'var(--as-ink)',
                                        }
                                      : {
                                          borderColor: 'var(--as-line)',
                                          color: 'var(--as-muted)',
                                        }
                                  }
                                >
                                  {DAY_LABELS[day] ?? day}
                                </button>
                              );
                            })}
                          </div>
                        </Field>
                      )}
                      {ask.special_instructions && (
                        <Field label="Anything the driver needs to know?">
                          <Textarea
                            value={form.special_instructions}
                            onChange={(v) => update('special_instructions', v)}
                            rows={2}
                            placeholder="e.g. Deliver to the back door off the alley — gate code #2714. Closed before 10am."
                          />
                        </Field>
                      )}
                    </Section>
                  )}

                  {step === 'equipment' && (
                    <Section
                      title="What equipment do you need?"
                      subtitle="Pick what the location needs and we'll build a lease or rental quote. Nothing here is a commitment — you'll see pricing before anything is signed."
                    >
                      <div className="space-y-2.5">
                        {EQUIPMENT_CHOICES.map((c) => {
                          const qty = equipment[c.category] ?? 0;
                          const on = qty > 0;
                          return (
                            <div
                              key={c.category}
                              className="flex items-center gap-3 rounded-xl border px-3.5 py-3 transition-colors"
                              style={{
                                borderColor: on ? 'var(--as-accent)' : 'var(--as-line)',
                                background: on ? 'var(--as-accent-soft)' : 'transparent',
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => setQty(c.category, on ? 0 : 1)}
                                aria-pressed={on}
                                className="flex-1 text-left cursor-pointer"
                              >
                                <div
                                  className="text-[14px] font-semibold"
                                  style={{ color: 'var(--as-ink)' }}
                                >
                                  {c.label}
                                </div>
                                <div className="text-[12px]" style={{ color: 'var(--as-muted)' }}>
                                  {c.hint}
                                </div>
                              </button>
                              {on ? (
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <button
                                    type="button"
                                    aria-label={`One fewer ${c.label}`}
                                    onClick={() => setQty(c.category, qty - 1)}
                                    className="as-btn-quiet h-8 w-8 inline-flex items-center justify-center"
                                  >
                                    <Minus size={14} />
                                  </button>
                                  <span
                                    className="w-6 text-center text-[14px] font-semibold tabular-nums"
                                    style={{ color: 'var(--as-ink)' }}
                                  >
                                    {qty}
                                  </span>
                                  <button
                                    type="button"
                                    aria-label={`One more ${c.label}`}
                                    onClick={() => setQty(c.category, qty + 1)}
                                    className="as-btn-quiet h-8 w-8 inline-flex items-center justify-center"
                                  >
                                    <Plus size={14} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setQty(c.category, 1)}
                                  className="as-btn-quiet shrink-0 px-3 py-1.5 text-[13px]"
                                >
                                  Add
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <Field label="Anything specific?" hint="Models you already run, space limits, timing.">
                        <Textarea
                          value={form.equipment_notes}
                          onChange={(v) => update('equipment_notes', v)}
                          rows={2}
                          placeholder="e.g. Replacing a 6-valve tower, tight back bar — about 30 inches."
                        />
                      </Field>
                      <p className="text-[12px]" style={{ color: 'var(--as-muted)' }}>
                        Skip this if you're not sure yet — you can start the equipment
                        conversation any time after the account is open.
                      </p>
                    </Section>
                  )}

                  {step === 'review' && (
                    <>
                      <Section
                        title="Review & send"
                        subtitle="Anything look wrong? Step back and fix it before sending."
                      >
                        <SummaryRow label="Location" value={form.store_name.trim() || '—'} />
                        {form.store_code.trim() && (
                          <SummaryRow label="Store number" value={form.store_code.trim()} />
                        )}
                        <SummaryRow label="Delivers to" value={addrBlob(addr) || '—'} />
                        <SummaryRow
                          label={ask.gm_contact ? 'Manager' : 'Contact'}
                          value={[form.gm_name.trim(), form.gm_email.trim()].filter(Boolean).join(' · ') || '—'}
                        />
                        {days.length > 0 && (
                          <SummaryRow
                            label="Delivery days"
                            value={days.map((d) => DAY_LABELS[d] ?? d).join(', ')}
                          />
                        )}
                        {ask.equipment && equipmentLines.length > 0 && (
                          <SummaryRow
                            label="Equipment"
                            value={equipmentLines.map((l) => `${l.qty} × ${l.label}`).join(', ')}
                          />
                        )}
                      </Section>

                      <Section title="Anything else we should know?">
                        <Textarea value={form.notes} onChange={(v) => update('notes', v)} rows={3} />
                      </Section>

                      {ask.terms && (
                        <Section title="Terms & Conditions">
                          <div
                            className="max-h-56 overflow-y-auto rounded-lg border px-4 py-3 text-xs leading-relaxed space-y-3"
                            style={{ borderColor: 'var(--as-line)', color: 'var(--as-ink)' }}
                          >
                            {APPLICATION_TERMS.map((s) => (
                              <div key={s.heading}>
                                <div className="font-semibold">{s.heading}</div>
                                {s.paragraphs.map((para, i) => (
                                  <p key={i} className="mt-1" style={{ color: 'var(--as-muted)' }}>
                                    {para}
                                  </p>
                                ))}
                              </div>
                            ))}
                            <p style={{ color: 'var(--as-muted)' }}>
                              Version {TERMS_VERSION} · Alameda Point Beverage Group Inc. (Brix Beverage)
                            </p>
                          </div>
                          <label className="flex items-start gap-2.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={termsAccepted}
                              onChange={(e) => setTermsAccepted(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span className="text-sm" style={{ color: 'var(--as-ink)' }}>
                              I have read and agree to the Terms &amp; Conditions above.{' '}
                              <span style={{ color: 'var(--as-warning)' }}>*</span>
                            </span>
                          </label>
                          <Field label="Authorized by (name & title)" required>
                            <Input
                              value={form.authorized_by}
                              onChange={(e) => update('authorized_by', e.target.value)}
                              placeholder="e.g. Jamie Doe, Owner"
                            />
                          </Field>
                        </Section>
                      )}

                      {ask.signature && (
                        <Field label="Signature" required>
                          <div
                            className="rounded-lg border bg-white h-32 overflow-hidden"
                            style={{ borderColor: 'var(--as-line)' }}
                          >
                            <SignaturePad ref={sigRef} onDirtyChange={setSigned} />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[11px]" style={{ color: 'var(--as-muted)' }}>
                              Sign with your finger or mouse.
                            </span>
                            {signed && (
                              <button
                                type="button"
                                onClick={() => sigRef.current?.clear()}
                                className="text-[11px] font-semibold cursor-pointer"
                                style={{ color: 'var(--as-primary)' }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </Field>
                      )}

                      {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="pt-1" />}
                    </>
                  )}

                  {error && (
                    <div
                      role="alert"
                      className="rounded-lg border px-3 py-2 text-sm"
                      style={{
                        borderColor: 'var(--as-warning)',
                        background: '#FBF4E8',
                        color: 'var(--as-warning)',
                      }}
                    >
                      {error}
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-1">
                    {stepIdx > 0 && (
                      <button type="button" onClick={goBack} className="as-btn-quiet px-4 py-2.5 text-sm">
                        Back
                      </button>
                    )}
                    {/* Always type="submit" — see onSubmit. Swapping the type
                        on this node mid-click is what submitted the form from
                        the step before. */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="as-btn flex-1 px-4 py-3 text-sm inline-flex items-center justify-center gap-2"
                    >
                      {submitting && <Loader2 size={16} className="animate-spin" />}
                      {isLast
                        ? submitting
                          ? 'Sending…'
                          : 'Send location details'
                        : 'Continue'}
                      {!isLast && <ArrowRight size={15} />}
                    </button>
                  </div>

                  <p className="text-xs text-center pt-1" style={{ color: 'var(--as-muted)' }}>
                    Already set up?{' '}
                    <Link
                      to="/login"
                      className="font-semibold no-underline"
                      style={{ color: 'var(--as-primary)' }}
                    >
                      Sign in
                    </Link>
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Progress rail. Labels on desktop, bars alone on mobile. */
function StepBar({
  steps,
  current,
}: {
  steps: Array<{ id: StepId; label: string }>;
  current: number;
}) {
  return (
    <div className="mt-5 mb-6" aria-label={`Step ${current + 1} of ${steps.length}`}>
      <div className="flex gap-1.5">
        {steps.map((s, i) => (
          <div
            key={s.id}
            className={`h-1 flex-1 rounded-full ${
              i < current ? 'as-step-done' : i === current ? 'as-step-on' : 'as-step-off'
            }`}
          />
        ))}
      </div>
      <div className="mt-2 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold" style={{ color: 'var(--as-ink)' }}>
          {steps[current]?.label}
        </span>
        <span className="text-[12px]" style={{ color: 'var(--as-muted)' }}>
          Step {current + 1} of {steps.length}
        </span>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="flex items-baseline gap-3 py-1.5 border-b last:border-0"
      style={{ borderColor: 'var(--as-line)' }}
    >
      <span className="text-[12px] w-28 shrink-0" style={{ color: 'var(--as-muted)' }}>
        {label}
      </span>
      <span className="text-[13px]" style={{ color: 'var(--as-ink)' }}>
        {value}
      </span>
    </div>
  );
}

/**
 * Success.
 *
 * The equipment block deliberately does NOT hand off to ERLS's public
 * equipment application. For a foodservice brand — The Melt, Starbird and
 * their franchisees — equipment requisition starts in the FOODSERVICE PORTAL
 * (melt-dashboard), which owns the catalog, the quote, inventory allocation,
 * shipping and install. ERLS owns only the lease instrument: the lease quote
 * math, the signed agreement and the monthly billing, and that quote is
 * created FROM the portal requisition by an operator — not by a store manager
 * filling in a credit application on a signup form.
 *
 * So the selection is a REQUISITION SEED, and the honest next step is "a
 * specialist will follow up", not a second form. See
 * docs/HANDOFF-FOODSERVICE-ERLS-SEAM.md.
 */
function Done({
  storeName,
  email,
  isFranchise,
  equipmentCount,
}: {
  storeName: string;
  email: string;
  isFranchise: boolean;
  equipmentCount: number;
}) {
  return (
    <div className="text-center py-8">
      <CheckCircle2 size={44} style={{ color: 'var(--as-primary)' }} className="mx-auto mb-4" />
      <h1
        className="as-display mb-2"
        style={{ fontSize: '22px', lineHeight: 1.2, color: 'var(--as-ink)' }}
      >
        Thanks — we've got it
      </h1>
      <p className="text-sm max-w-sm mx-auto" style={{ color: 'var(--as-muted)' }}>
        We've received the details for {storeName} and sent a confirmation to {email}. Our team
        will set it up and email you when it's ready to order
        {isFranchise ? ' — billing on the same account as your other locations' : ''}.
      </p>

      {equipmentCount > 0 && (
        <div
          className="mt-6 rounded-xl border px-5 py-5 text-left"
          style={{ borderColor: 'var(--as-accent)', background: 'var(--as-accent-soft)' }}
        >
          <div className="as-eyebrow">About your equipment</div>
          <div
            className="as-display mt-1 mb-1.5"
            style={{ fontSize: '17px', color: 'var(--as-ink)' }}
          >
            We'll put a quote together
          </div>
          <p className="text-[13px]" style={{ color: 'var(--as-muted)' }}>
            You picked {equipmentCount} {equipmentCount === 1 ? 'item' : 'items'} — it's on file
            with your store. A specialist will price it for the space and come back to you with
            options to buy or lease. Nothing is ordered or committed yet.
          </p>
        </div>
      )}

      <p className="text-sm mt-5" style={{ color: 'var(--as-muted)' }}>
        Questions? Call us at{' '}
        <a
          href={SUPPORT_TEL_HREF}
          className="font-semibold no-underline"
          style={{ color: 'var(--as-primary)' }}
        >
          {SUPPORT_PHONE}
        </a>
        .
      </p>
    </div>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  return (
    <div className="text-center py-10">
      <h1
        className="as-display mb-2"
        style={{ fontSize: '19px', color: 'var(--as-ink)' }}
      >
        {title}
      </h1>
      <p className="text-sm max-w-sm mx-auto" style={{ color: 'var(--as-muted)' }}>
        {body}
      </p>
      <p className="text-sm mt-4" style={{ color: 'var(--as-muted)' }}>
        Call us at{' '}
        <a
          href={SUPPORT_TEL_HREF}
          className="font-semibold no-underline"
          style={{ color: 'var(--as-primary)' }}
        >
          {SUPPORT_PHONE}
        </a>{' '}
        and we'll sort it out.
      </p>
    </div>
  );
}
